import { useEffect, useMemo, useRef, useState } from 'react';

// LocalStorage keys (kept compatible with assistant project)
const LS_KEY_CONFIG = 'aiassistant:config:v1';

// Default behavior (no UI in sboard, but config is still supported)
const defaultConfig = {
  publicId: '',
  currentStopId: '',
  model: 'openai',
  selectedVoice: 'auto',
  autoTts: true,
};

function loadConfig() {
  try {
    const raw = localStorage.getItem(LS_KEY_CONFIG);
    if (!raw) return { ...defaultConfig };
    const parsed = JSON.parse(raw);
    return { ...defaultConfig, ...(parsed || {}) };
  } catch {
    return { ...defaultConfig };
  }
}

function saveConfig(cfg) {
  try {
    localStorage.setItem(LS_KEY_CONFIG, JSON.stringify(cfg));
  } catch {
    // ignore
  }
}

function nowMs() {
  return Date.now();
}

function getAiBaseUrl() {
  // AI assistant API lives under the main API base.
  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();
  if (!apiBase) return '';
  return apiBase.replace(/\/$/, '') + '/api/ai-assistant';
}

function getSBoardIdSafe() {
  try {
    const parts = String(window.location.pathname || '')
      .split('/')
      .map((p) => String(p || '').trim())
      .filter(Boolean);
    if (!parts.length) return '';

    // Our routes may optionally append "/debug" or similar.
    const last = String(parts[parts.length - 1] || '').toLowerCase();
    if (last === 'debug' || last === 'assistantdebug') {
      return String(parts[parts.length - 2] || '').trim();
    }
    return String(parts[parts.length - 1] || '').trim();
  } catch {
    return '';
  }
}

function buildAskUrl({ baseUrl, publicId, stopId, model }) {
  const base = String(baseUrl || '').replace(/\/$/, '');
  return (
    base +
    '/public/ai/stop/ask?publicId=' +
    encodeURIComponent(publicId) +
    '&currentStopId=' +
    encodeURIComponent(stopId) +
    '&model=' +
    encodeURIComponent(model || 'openai')
  );
}

function getVoicesSafe() {
  try {
    if (!('speechSynthesis' in window)) return [];
    return window.speechSynthesis.getVoices() || [];
  } catch {
    return [];
  }
}

function pickVoice(voices, selectedVoice) {
  if (!voices || !voices.length) return undefined;

  if (!selectedVoice || selectedVoice === 'auto') {
    return (
      voices.find((v) => String(v.name || '').toLowerCase().includes('tetiana')) ||
      voices.find(
        (v) =>
          String(v.name || '').toLowerCase().includes('ukrainian_tts') &&
          String(v.name || '').toLowerCase().includes('medium')
      ) ||
      voices.find((v) => v.lang === 'uk-UA') ||
      voices.find((v) => String(v.lang || '').toLowerCase().startsWith('uk'))
    );
  }

  return voices.find((v) => v.name === selectedVoice);
}

function createBeep(freq, durationMs, gainValue) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.value = gainValue;

    osc.connect(gain);
    gain.connect(ctx.destination);

    const t0 = ctx.currentTime;
    osc.start(t0);
    osc.stop(t0 + durationMs / 1000);

    osc.onended = () => {
      try {
        ctx.close();
      } catch {
        // ignore
      }
    };
  } catch {
    // ignore
  }
}

/**
 * Background voice assistant for Stop-Board:
 * - PTT on F8 (keydown starts, keyup stops)
 * - SpeechRecognition -> POST /public/ai/stop/ask
 * - TTS speaks server response (tts field) and locks re-entry until finished
 * - If busy/speaking: negative beep + (optionally) "Зачекай: відповідь ще обробляється." throttled
 * - Optional max record duration (default 30s)
 */
export default function useAiAssistantVoiceBackground({ publicId, stopId, maxRecordSeconds = 30 }) {
  const cfgRef = useRef(loadConfig());

  // Lightweight UI state (can be used for optional visual indicators)
  const [ui, setUi] = useState(() => ({
    mode: 'idle', // idle | listening | processing | speaking | error
    text: 'F8 — утримуй, щоб говорити',
    transcript: '',
  }));

  const setUiSafe = (partial) => {
    setUi((prev) => {
      const next = { ...prev, ...(partial || {}) };
      // Avoid re-render storms if nothing changed
      if (next.mode === prev.mode && next.text === prev.text && next.transcript === prev.transcript) return prev;
      return next;
    });
  };

  const recognitionRef = useRef(null);
  const voicesRef = useRef([]);

  const isListeningRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const busyRef = useRef(false);

  const f8DownRef = useRef(false);
  const startedByF8Ref = useRef(false);

  const lastTranscriptRef = useRef('');
  const transcriptFlushTimerRef = useRef(null);
  const lastWaitTtsAtRef = useRef(0);
  const lastBusyBeepAtRef = useRef(0);
  const lastEndBeepAtRef = useRef(0);

  const ttsUnlockTimerRef = useRef(null);
  const recordLimitTimerRef = useRef(null);

  // Failsafes (prevent “stuck” states)
  const uiResetTimerRef = useRef(null);
  const forceEndTimerRef = useRef(null);
  const srSessionIdRef = useRef(0);


  const WAIT_TTS = 'Зачекай: відповідь ще обробляється.';
  const ERROR_TTS = 'Немає зв’язку з сервером. Спробуйте пізніше.';

  const defaultIdleText = useMemo(() => 'F8 — утримуй, щоб говорити', []);

  // Keep config aligned with route params (no UI)
  useEffect(() => {
    const cfg = cfgRef.current || { ...defaultConfig };
    const next = {
      ...cfg,
      publicId: publicId || cfg.publicId || '',
      currentStopId: stopId || cfg.currentStopId || '',
    };
    cfgRef.current = next;
    saveConfig(next);
  }, [publicId, stopId]);

  // Voices loading (speechSynthesis may populate asynchronously)
  useEffect(() => {
    const updateVoices = () => {
      voicesRef.current = getVoicesSafe();
    };
    updateVoices();
    if ('speechSynthesis' in window && window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = updateVoices;
    }
    return () => {
      if ('speechSynthesis' in window && window.speechSynthesis.onvoiceschanged === updateVoices) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const clearTtsUnlockTimer = () => {
    if (ttsUnlockTimerRef.current) {
      window.clearTimeout(ttsUnlockTimerRef.current);
      ttsUnlockTimerRef.current = null;
    }
  };

  const clearTranscriptFlushTimer = () => {
    if (transcriptFlushTimerRef.current) {
      window.clearTimeout(transcriptFlushTimerRef.current);
      transcriptFlushTimerRef.current = null;
    }
  };

  const clearRecordLimitTimer = () => {
    if (recordLimitTimerRef.current) {
      window.clearTimeout(recordLimitTimerRef.current);
      recordLimitTimerRef.current = null;
    }
  };

  const clearUiResetTimer = () => {
    if (uiResetTimerRef.current) {
      window.clearTimeout(uiResetTimerRef.current);
      uiResetTimerRef.current = null;
    }
  };

  const clearForceEndTimer = () => {
    if (forceEndTimerRef.current) {
      window.clearTimeout(forceEndTimerRef.current);
      forceEndTimerRef.current = null;
    }
  };

  const softResetToIdleLater = (ms = 2500) => {
    clearUiResetTimer();
    uiResetTimerRef.current = window.setTimeout(() => {
      uiResetTimerRef.current = null;
      if (!busyRef.current && !isSpeakingRef.current && !isListeningRef.current) {
        setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
      }
    }, Math.max(0, Number(ms) || 0));
  };

  const speakText = (text, { lockUI, cancelExisting }) => {
    const cfg = cfgRef.current || defaultConfig;
    if (!cfg.autoTts) return;
    if (!('speechSynthesis' in window)) return;

    if (cancelExisting) {
      clearTtsUnlockTimer();
      try {
        window.speechSynthesis.cancel();
      } catch {
        // ignore
      }
    }

    if (lockUI) {
      isSpeakingRef.current = true;
      setUiSafe({ mode: 'speaking', text: 'Озвучую відповідь…' });
    }

    const utterance = new SpeechSynthesisUtterance(String(text || ''));
    utterance.lang = 'uk-UA';
    utterance.rate = 1;
    utterance.pitch = 1;

    const voice = pickVoice(voicesRef.current, cfg.selectedVoice);
    if (voice) utterance.voice = voice;

    const finish = () => {
      if (lockUI) isSpeakingRef.current = false;
      clearTtsUnlockTimer();

      // Restore UI if we are not processing/listening
      if (!busyRef.current && !isListeningRef.current) {
        setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
      }
    };

    utterance.onend = finish;
    utterance.onerror = finish;

    // Watchdog for lockUI=true
    if (lockUI) {
      const approxMs = Math.min(90_000, Math.max(6_000, Math.ceil(String(text || '').length / 10) * 1000));
      ttsUnlockTimerRef.current = window.setTimeout(() => {
        isSpeakingRef.current = false;
        clearTtsUnlockTimer();
        if (!busyRef.current && !isListeningRef.current) {
          setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
        }
      }, approxMs + 1500);
    }

    try {
      window.speechSynthesis.speak(utterance);
    } catch {
      finish();
    }
  };

  const speakResponseLocked = (text) => speakText(text, { lockUI: true, cancelExisting: true });
  const speakNonBlocking = (text) => speakText(text, { lockUI: false, cancelExisting: false });

  const beepStart = () => createBeep(880, 120, 0.08);
  const beepEnd = () => {
    const t = nowMs();
    if (t - lastEndBeepAtRef.current < 200) return;
    lastEndBeepAtRef.current = t;
    // two quick beeps
    createBeep(660, 70, 0.06);
    window.setTimeout(() => createBeep(990, 70, 0.06), 90);
  };
  const beepNegativeBusy = () => {
    const t = nowMs();
    if (t - lastBusyBeepAtRef.current < 600) return;
    lastBusyBeepAtRef.current = t;
    createBeep(220, 100, 0.10);
  };

  const speakWaitIfAllowed = () => {
    const cfg = cfgRef.current || defaultConfig;
    // Only speak WAIT while request is in-flight and we are NOT speaking the answer (do not interrupt answer)
    if (!cfg.autoTts || !busyRef.current || isSpeakingRef.current) return;
    const t = nowMs();
    if (t - lastWaitTtsAtRef.current < 2500) return;
    lastWaitTtsAtRef.current = t;
    speakNonBlocking(WAIT_TTS);
  };

  const busyFeedback = () => {
    beepNegativeBusy();
    setUiSafe({ mode: 'processing', text: WAIT_TTS });
    speakWaitIfAllowed();
  };

  const stopRecognition = () => {
    clearRecordLimitTimer();
    clearForceEndTimer();

    const sessionId = srSessionIdRef.current;

    try {
      if (recognitionRef.current) recognitionRef.current.stop();
    } catch {
      // ignore
    }

    // Failsafe: if onend doesn't fire (common in noisy environments / focus issues),
    // force-unblock and optionally send what we already captured.
    forceEndTimerRef.current = window.setTimeout(() => {
      forceEndTimerRef.current = null;
      if (srSessionIdRef.current !== sessionId) return;
      if (!isListeningRef.current) return;

      // Try abort to ensure the WebSpeech stack stops.
      try {
        if (recognitionRef.current) recognitionRef.current.abort();
      } catch {
        // ignore
      }

      isListeningRef.current = false;
      beepEnd();

      const transcript = String(lastTranscriptRef.current || '').trim();
      const started = startedByF8Ref.current;

      // Reset flags to avoid double-send if onend fires later
      startedByF8Ref.current = false;
      lastTranscriptRef.current = '';
      f8DownRef.current = false;
      clearTranscriptFlushTimer();

      if (started && transcript) {
        setUiSafe({ mode: 'processing', text: 'Обробляю запит…', transcript: '' });
        void sendTranscript(transcript);
        return;
      }

      // No transcript => return to idle
      if (!busyRef.current && !isSpeakingRef.current) {
        setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
      } else {
        softResetToIdleLater(2000);
      }
    }, 1200);
  };

  const cancelRecording = () => {
    // Stop SR WITHOUT sending (used on blur/visibility changes)
    startedByF8Ref.current = false;
    lastTranscriptRef.current = '';
    f8DownRef.current = false;
    clearTranscriptFlushTimer();
    clearRecordLimitTimer();
    clearForceEndTimer();
    isListeningRef.current = false;

    try {
      if (recognitionRef.current) recognitionRef.current.abort();
    } catch {
      try {
        if (recognitionRef.current) recognitionRef.current.stop();
      } catch {
        // ignore
      }
    }

    setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
  };

  const sendTranscript = async (transcript) => {
    const cfg = cfgRef.current || defaultConfig;
    const baseUrl = getAiBaseUrl();

    const pid = (cfg.publicId || '').trim();
    const sid = (cfg.currentStopId || '').trim();
    const model = (cfg.model || 'openai').trim();

    const cleaned = String(transcript || '').trim();
    if (!cleaned) return;

    const sboardId = getSBoardIdSafe();

    if (!baseUrl || !pid || !sid) {
      setUiSafe({ mode: 'error', text: ERROR_TTS, transcript: '' });
      speakResponseLocked(ERROR_TTS);
      softResetToIdleLater(3500);
      return;
    }

    busyRef.current = true;
    setUiSafe({ mode: 'processing', text: 'Обробляю запит…', transcript: '' });
    try {
      const url = buildAskUrl({ baseUrl, publicId: pid, stopId: sid, model });
      const ctrl = new AbortController();
      const timeout = window.setTimeout(() => ctrl.abort(), 25_000);

      const headers = { 'Content-Type': 'text/plain' };
      if (sboardId) headers.SBoardId = sboardId;

      let res;
      try {
        res = await fetch(url, {
          method: 'POST',
          headers,
          body: cleaned,
          credentials: 'include',
          signal: ctrl.signal,
        });
      } finally {
        window.clearTimeout(timeout);
      }

      const rawBody = await res.text();
      if (!rawBody || !rawBody.trim()) throw new Error('empty_response');
      if (!res.ok) throw new Error('bad_status');

      let parsed;
      try {
        parsed = JSON.parse(rawBody);
      } catch {
        throw new Error('bad_json');
      }

      const displayText = String(parsed?.text || '').trim();
      const ttsText = String(parsed?.tts || parsed?.text || '').trim();
      if (!displayText) throw new Error('empty_text');

      // Speak assistant answer (lock until finished)
      if (cfg.autoTts && ttsText) {
        speakResponseLocked(ttsText);
      } else {
        // No TTS => ensure we return to idle shortly
        softResetToIdleLater(800);
      }
    } catch {
      // Speak error (lock until finished)
      setUiSafe({ mode: 'error', text: ERROR_TTS, transcript: '' });
      speakResponseLocked(ERROR_TTS);
      softResetToIdleLater(3500);
    } finally {
      busyRef.current = false;

      // If we did not enter speaking (autoTts off), return to idle
      if (!isSpeakingRef.current && !isListeningRef.current) {
        setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
      }
    }
  };

  const startRecognition = () => {
    // Must have recognition
    if (!recognitionRef.current) return;

    // If busy or speaking => feedback and do not start.
    if (busyRef.current || isSpeakingRef.current) {
      busyFeedback();
      return;
    }

    // Cancel any TTS before listening (avoid capturing TTS)
    try {
      clearTtsUnlockTimer();
      window.speechSynthesis.cancel();
      isSpeakingRef.current = false;
    } catch {
      // ignore
    }

    // New SR session
    srSessionIdRef.current += 1;
    clearForceEndTimer();
    clearTranscriptFlushTimer();

    startedByF8Ref.current = true;
    lastTranscriptRef.current = '';

    // Start beep and then start SR
    beepStart();
    setUiSafe({ mode: 'listening', text: 'Говори… (утримуй F8)', transcript: '' });
    try {
      recognitionRef.current.start();
    } catch {
      // ignore
    }

    // Optional hard cap
    clearRecordLimitTimer();
    if (maxRecordSeconds && Number(maxRecordSeconds) > 0) {
      recordLimitTimerRef.current = window.setTimeout(() => {
        // Force-stop and send what we have.
        f8DownRef.current = false;
        stopRecognition();
      }, Number(maxRecordSeconds) * 1000);
    }
  };

  // SpeechRecognition init
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return undefined;

    const recognition = new SpeechRecognition();
    // Best-practice for kiosk/noisy env: final results only, stop on silence or manual stop.
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'uk-UA';

    recognition.onstart = () => {
      isListeningRef.current = true;
      setUiSafe({ mode: 'listening', text: 'Говори… (утримуй F8)', transcript: '' });
    };

    recognition.onresult = (event) => {
      // Final-only transcript
      let text = '';
      for (let i = event.resultIndex; i < (event.results?.length || 0); i++) {
        const res = event.results[i];
        const part = String(res?.[0]?.transcript || '').trim();
        if (!part) continue;
        text += (text ? ' ' : '') + part;
      }

      const transcript = String(text || '').trim();
      if (!transcript) return;
      lastTranscriptRef.current = transcript;

      // Optional: show what was recognized (debug bar)
      if (!transcriptFlushTimerRef.current) {
        transcriptFlushTimerRef.current = window.setTimeout(() => {
          transcriptFlushTimerRef.current = null;
          setUiSafe({ transcript: String(lastTranscriptRef.current || '') });
        }, 80);
      }
    };

    recognition.onerror = () => {
      isListeningRef.current = false;
      startedByF8Ref.current = false;
      lastTranscriptRef.current = '';
      f8DownRef.current = false;
      clearRecordLimitTimer();
      clearTranscriptFlushTimer();
      clearForceEndTimer();
      setUiSafe({ mode: 'error', text: 'Не вдалося розпізнати. Спробуй ще раз.', transcript: '' });
      // End beep to indicate stop
      beepEnd();
      softResetToIdleLater(2500);
    };

    recognition.onend = () => {
      isListeningRef.current = false;
      clearRecordLimitTimer();
      clearTranscriptFlushTimer();
      clearForceEndTimer();

      // End beep when we stop listening (regardless of why)
      beepEnd();

      const transcript = String(lastTranscriptRef.current || '').trim();
      startedByF8Ref.current = false;
      lastTranscriptRef.current = '';
      f8DownRef.current = false;

      // If nothing was recognized (silence/noise), return to idle so user can retry
      if (!transcript) {
        if (!busyRef.current && !isSpeakingRef.current) {
          setUiSafe({ mode: 'idle', text: defaultIdleText, transcript: '' });
        } else {
          softResetToIdleLater(1500);
        }
        return;
      }

      // Immediately show processing hint while request is sent
      setUiSafe({ mode: 'processing', text: 'Обробляю запит…', transcript: '' });
      void sendTranscript(transcript);
    };

    recognitionRef.current = recognition;

    return () => {
      recognitionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose PTT handlers for optional on-screen button (same behavior as F8)
  const startPtt = () => {
    if (busyRef.current || isSpeakingRef.current) {
      busyFeedback();
      return;
    }
    if (f8DownRef.current) return;
    f8DownRef.current = true;
    startRecognition();
  };

  const stopPtt = () => {
    const wasDown = f8DownRef.current;
    f8DownRef.current = false;
    if (wasDown && (isListeningRef.current || startedByF8Ref.current)) {
      stopRecognition();
    }
  };

  // Hotkey: F8 Push-to-Talk (hold to record)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code !== 'F8') return;
      if (e.repeat) return;
      e.preventDefault();

      // If busy/speaking -> feedback and do NOT start
      if (busyRef.current || isSpeakingRef.current) {
        busyFeedback();
        return;
      }

      // Recovery: if keyup was missed, a new press should stop the current SR
      if (isListeningRef.current || startedByF8Ref.current) {
        f8DownRef.current = false;
        stopRecognition();
        return;
      }

      f8DownRef.current = true;
      startRecognition();
    };

    const onKeyUp = (e) => {
      if (e.code !== 'F8') return;
      e.preventDefault();
      f8DownRef.current = false;

      // Stop even if onstart flag didn't update yet (race-safe)
      if (isListeningRef.current || startedByF8Ref.current) stopRecognition();
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp, { passive: false });

    const onBlur = () => {
      if (isListeningRef.current) cancelRecording();
    };

    const onVisibility = () => {
      if (document.hidden && isListeningRef.current) cancelRecording();
    };

    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxRecordSeconds]);

  // Return UI state + handlers (safe to ignore by callers)
  return {
    ui,
    startPtt,
    stopPtt,
    isListening: ui.mode === 'listening',
    isBusy: ui.mode === 'processing',
    isSpeaking: ui.mode === 'speaking',
  };
}
